import { useEffect, useRef, useState } from 'react';

import { IUser } from '../../../../models/user/IUser';
import { reportPwaClient } from '../api/reportPwaClient';
import { IPwaClientSyncResult } from '../types';
import { detectPwaClient } from '../utils/detectPwaClient';
import { markClientSynced, shouldSyncPwaClient } from '../utils/pwaClientLocalState';

export interface IUsePwaClientTrackingOptions {
    user: IUser | undefined;
    token: string | undefined;
    win?: Window;
    reportFn?: typeof reportPwaClient;
}

export interface IUsePwaClientTrackingResult {
    client: 'web' | 'pwa';
    lastResult: IPwaClientSyncResult | null;
    isSyncing: boolean;
}

/**
 * Logged-in app boot (+ resume) sync: reports web vs installed PWA.
 * Throttled per user. PWA opens use a shorter window so last_pwa_open_at
 * updates when the user comes back without logging in again.
 */
export function usePwaClientTracking(options: IUsePwaClientTrackingOptions): IUsePwaClientTrackingResult {
    const { user, token, win = window, reportFn = reportPwaClient } = options;
    const [lastResult, setLastResult] = useState<IPwaClientSyncResult | null>(null);
    const [isSyncing, setIsSyncing] = useState(false);
    // One in-flight attempt key. Cleared when the app becomes visible again so
    // a resume after the throttle window can refresh last_pwa_open_at.
    const attemptedRef = useRef<string | null>(null);
    const inFlightRef = useRef(false);

    const client = detectPwaClient(win);

    useEffect(() => {
        const userId = user?.Userid;
        if (!user || !token || !userId) {
            return undefined;
        }

        const runSync = () => {
            if (inFlightRef.current) {
                return;
            }

            const attemptKey = `${userId}:${client}`;
            if (attemptedRef.current === attemptKey) {
                return;
            }

            if (!shouldSyncPwaClient({ userId, client, storage: win.localStorage })) {
                return;
            }

            attemptedRef.current = attemptKey;
            inFlightRef.current = true;
            setIsSyncing(true);

            void (async () => {
                try {
                    const result = await reportFn({ user, token, client });
                    setLastResult(result);

                    // Only throttle when the DB actually wrote. Tracked:0 means
                    // the migration is missing — keep the attempt marker for this
                    // mount (no loop), and retry on the next boot / resume.
                    if (result.status === 'synced' && result.tracked !== false) {
                        markClientSynced(userId, client, Date.now(), win.localStorage);
                    }
                } finally {
                    inFlightRef.current = false;
                    setIsSyncing(false);
                }
            })();
        };

        runSync();

        // Mobile PWAs often resume from background without remounting React.
        // Re-check so last_pwa_open_at can move after the throttle window.
        const onResume = () => {
            const doc = win.document;
            if (doc && doc.visibilityState && doc.visibilityState !== 'visible') {
                return;
            }
            attemptedRef.current = null;
            runSync();
        };

        win.document?.addEventListener?.('visibilitychange', onResume);
        win.addEventListener?.('pageshow', onResume);
        win.addEventListener?.('focus', onResume);

        return () => {
            win.document?.removeEventListener?.('visibilitychange', onResume);
            win.removeEventListener?.('pageshow', onResume);
            win.removeEventListener?.('focus', onResume);
        };
    }, [user, token, client, win, reportFn]);

    return { client, lastResult, isSyncing };
}
